import PropTypes from "prop-types";
import { useMemo } from "react";
import { Polyline, Rectangle } from "react-leaflet";

import { Map } from "@/utils";

const GRIDSTYLE = {
    color: "#CCCCCC",
    weight: 1,
    opacity: 1,
    interactive: false,
};

const BORDERSTYLE = {
    color: "#CCCCCC",
    weight: 1,
    opacity: 1,
    interactive: false,
};

export const FEETINMETER = 3.28084;

const highlightOptions = {
    color: "#FCB414",
    fillOpacity: 0.1,
    weight: 0,
};

/**
 * Renders ceiling grid polylines and highlighted rectangles on the map.
 *
 * @param root0 - The component props.
 * @param root0.mapSettings - The map settings containing ceiling grid configuration.
 * @param root0.showInternalLines - Whether to render internal grid lines.
 * @param root0.highlight - The highlight configuration for row/column emphasis.
 * @returns The rendered grid layer.
 */
const GridLayer = ({ mapSettings, showInternalLines, highlight }) => {
    const elements = useMemo(() => {
        if (!mapSettings?.ceilingDetails || !mapSettings?.spaceDetails)
            return null;

        const { ceilingDetails, spaceDetails } = mapSettings;
        const xGrid = ceilingDetails.tileSizeX / FEETINMETER;
        const yGrid = ceilingDetails.tileSizeY / FEETINMETER;
        const gridOffsetX = ceilingDetails.gridOffSetX / FEETINMETER;
        const gridOffsetY = ceilingDetails.gridOffSetY / FEETINMETER;
        const initialValueY = 0 + gridOffsetY;
        const initialValueX = 0 + gridOffsetX;
        const floorHeight = Map.Converter.convertFeetToMeters(
            spaceDetails.height,
        );
        const floorWidth = Map.Converter.convertFeetToMeters(
            spaceDetails.width,
        );

        const polylines = [];
        const rectangles = [];
        const highlighted = {};

        // Horizontal Border Lines
        let rowLine = [
            [initialValueY, initialValueX],
            [initialValueY, floorWidth + gridOffsetX],
        ];
        const lastRowLine = [
            [floorHeight + gridOffsetY, initialValueX],
            [floorHeight + gridOffsetY, floorWidth + gridOffsetX],
        ];
        polylines.push(
            <Polyline
                key="hborder1"
                positions={rowLine}
                pathOptions={BORDERSTYLE}
            />,
            <Polyline
                key="hborder2"
                positions={lastRowLine}
                pathOptions={BORDERSTYLE}
            />,
        );

        // Vertical Border Lines
        let columnLine = [
            [initialValueY, initialValueX],
            [floorHeight + gridOffsetY, initialValueX],
        ];
        const lastColumnLine = [
            [initialValueY, floorWidth + gridOffsetX],
            [floorHeight + gridOffsetY, floorWidth + gridOffsetX],
        ];
        polylines.push(
            <Polyline
                key="vborder1"
                positions={columnLine}
                pathOptions={BORDERSTYLE}
            />,
            <Polyline
                key="vborder2"
                positions={lastColumnLine}
                pathOptions={BORDERSTYLE}
            />,
        );

        const totalValueY = yGrid > 0 ? Math.ceil(floorHeight / yGrid) : 0;
        const totalValueX = xGrid > 0 ? Math.ceil(floorWidth / xGrid) : 0;

        // Horizontal Grid Lines
        for (let y = 0; y < totalValueY; y++) {
            const distanceY = y * yGrid;
            const l1a = [distanceY + gridOffsetY, initialValueX];
            const l1b = [distanceY + gridOffsetY, floorWidth + gridOffsetX];
            if (
                !highlighted.row &&
                highlight?.row &&
                distanceY >= highlight.row
            ) {
                highlighted.row = y + 1;
                rectangles.push(
                    <Rectangle
                        key={`row-highlight-${y}`}
                        bounds={[rowLine, [l1a, l1b]]}
                        pathOptions={highlightOptions}
                    />,
                );
            }
            rowLine = [l1a, l1b];
            if (distanceY < floorHeight && showInternalLines) {
                polylines.push(
                    <Polyline
                        key={`hgrid-${y}`}
                        positions={[l1a, l1b]}
                        pathOptions={GRIDSTYLE}
                    />,
                );
            }
        }

        // Vertical Grid Lines
        for (let x = 0; x < totalValueX; x++) {
            const distanceX = x * xGrid;
            const l2a = [initialValueY, distanceX + gridOffsetX];
            const l2b = [floorHeight + gridOffsetY, distanceX + gridOffsetX];
            if (
                !highlighted.column &&
                highlight?.column &&
                distanceX >= highlight.column
            ) {
                highlighted.column = x + 1;
                rectangles.push(
                    <Rectangle
                        key={`col-highlight-${x}`}
                        bounds={[columnLine, [l2a, l2b]]}
                        pathOptions={highlightOptions}
                    />,
                );
            }
            columnLine = [l2a, l2b];
            if (distanceX < floorWidth && showInternalLines) {
                polylines.push(
                    <Polyline
                        key={`vgrid-${x}`}
                        positions={[l2b, l2a]}
                        pathOptions={GRIDSTYLE}
                    />,
                );
            }
        }

        if (!highlighted.row && highlight?.row) {
            rectangles.push(
                <Rectangle
                    key="row-highlight-last"
                    bounds={[rowLine, lastRowLine]}
                    pathOptions={highlightOptions}
                />,
            );
        }

        if (!highlighted.column && highlight?.column) {
            rectangles.push(
                <Rectangle
                    key="col-highlight-last"
                    bounds={[columnLine, lastColumnLine]}
                    pathOptions={highlightOptions}
                />,
            );
        }

        return [...polylines, ...rectangles];
    }, [mapSettings, showInternalLines, highlight]);

    return <>{elements}</>;
};

GridLayer.propTypes = {
    mapSettings: PropTypes.shape({
        ceilingDetails: PropTypes.shape({
            tileSizeX: PropTypes.number.isRequired,
            tileSizeY: PropTypes.number.isRequired,
            gridOffSetX: PropTypes.number.isRequired,
            gridOffSetY: PropTypes.number.isRequired,
        }),
        spaceDetails: PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired,
        }),
    }),
    showInternalLines: PropTypes.bool,
    highlight: PropTypes.shape({
        row: PropTypes.number,
        column: PropTypes.number,
    }),
};

export default GridLayer;
